import { PropsWithChildren, useEffect } from 'react'

import logger from '@cash-web/shared/util-logger'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { BlockerComponentProps, BlockerType, useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { setBlockerDescriptorId } from '@cash-web/shared-util-fetch'
import { WebViewViewBlocker } from '@squareup/customer-data-events-web'

import { getRegisteredComponent } from '../blockerComponentRegistry'
import { BlockerRedirectToMobile } from '../BlockerRedirectToMobile/BlockerRedirectToMobile'
import { BlockerStatusResult } from '../BlockerStatusResult/BlockerStatusResult'

export const FlowRenderer = ({ children }: PropsWithChildren) => {
  const flow = useFlowContext()
  const logCdfEvent = useLogCashWebCdfEvent()

  const { blockerData, statusResult } = flow?.active ?? {}

  const Component = blockerData
    ? (getRegisteredComponent(blockerData) as React.FC<BlockerComponentProps<BlockerType>>)
    : null

  useEffect(() => {
    const isSupported = Component != null
    const isStatusResult = (blockerData == null || blockerData.type == null) && statusResult != null
    // we do not need to log for status results as we already have a separate event for them
    if (!isStatusResult) {
      logger.addBreadcrumb('FlowRenderer', {
        clientScenario: flow.active?.clientScenario,
        trackingFlowName: flow.active?.trackingFlowName,
        blockerId: blockerData?.id,
        blockerType: blockerData?.type,
        groupId: blockerData?.groupId,
        skippable: blockerData?.skippable,
        originatingFlowType: flow.active?.originatingFlowType,
        isSupported,
      })

      logCdfEvent(
        WebViewViewBlocker({
          clientScenario: flow.active?.clientScenario,
          trackingFlowName: flow.active?.trackingFlowName,
          blockerId: blockerData?.id,
          blockerType: blockerData?.type,
          groupId: blockerData?.groupId,
          skippable: blockerData?.skippable,
          originatingFlowType: flow.active?.originatingFlowType,
          isSupported,
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Component, blockerData?.id, blockerData?.type])

  // do not render if we are no longer in progress
  if (!flow.isInProgress) {
    return null
  }

  if (blockerData == null || blockerData.type == null) {
    if (statusResult != null) {
      return (
        <BlockerStatusResult
          result={statusResult}
          onClose={() => {
            flow.complete()
          }}
        />
      )
    }
    return null
  }

  const ComponentOrRedirectToMobile = Component ?? BlockerRedirectToMobile

  setBlockerDescriptorId({
    id: blockerData?.id,
    groupId: blockerData?.groupId,
  })

  return <ComponentOrRedirectToMobile key={blockerData.id} {...blockerData} />
}
